// Dividend tax rates per country, used by the dividend calculator.
//
// `rate` is the headline domestic tax applied to dividend income for a typical
// resident retail investor. Where no clear published headline exists, we apply
// a conservative regional average and mark `isEstimate: true`.
//
// Country display names are NOT stored here — they are resolved at render time
// via `Intl.DisplayNames(locale, { type: "region" })` so the picker works in
// every site language without 200 × 12 hard-coded strings.

export type Region = "europe" | "americas" | "asiaPacific" | "mea" | "other";

export interface DividendTaxRate {
  code: string; // ISO-3166 alpha-2 (or "CUSTOM" / "UK" legacy alias)
  rate: number; // 0.0 - 1.0
  region: Region;
  isEstimate?: boolean;
  /** Optional currency override for display (defaults to EUR). */
  currency?: string;
}

export const WORLD_AVERAGE_RATE = 0.2;

// Regional averages used as fallback for countries without a documented headline.
const REGION_AVG: Record<Region, number> = {
  europe: 0.21,
  americas: 0.12,
  asiaPacific: 0.14,
  mea: 0.1,
  other: WORLD_AVERAGE_RATE,
};

// Documented rates. Anything not listed falls back to its region average.
// Sources: national tax authorities, Deloitte / PwC tax summaries 2024-2025.
const KNOWN: Record<string, { rate: number; region: Region; currency?: string }> = {
  // Europe
  DE: { rate: 0.26375, region: "europe", currency: "EUR" },
  GB: { rate: 0.0875, region: "europe", currency: "GBP" },
  FR: { rate: 0.3, region: "europe", currency: "EUR" },
  ES: { rate: 0.19, region: "europe", currency: "EUR" },
  IT: { rate: 0.26, region: "europe", currency: "EUR" },
  NL: { rate: 0.15, region: "europe", currency: "EUR" },
  BE: { rate: 0.3, region: "europe", currency: "EUR" },
  AT: { rate: 0.275, region: "europe", currency: "EUR" },
  IE: { rate: 0.41, region: "europe", currency: "EUR" },
  PT: { rate: 0.28, region: "europe", currency: "EUR" },
  LU: { rate: 0.15, region: "europe", currency: "EUR" },
  CH: { rate: 0.35, region: "europe", currency: "CHF" },
  SE: { rate: 0.3, region: "europe", currency: "SEK" },
  NO: { rate: 0.3784, region: "europe", currency: "NOK" },
  DK: { rate: 0.27, region: "europe", currency: "DKK" },
  FI: { rate: 0.255, region: "europe", currency: "EUR" },
  IS: { rate: 0.22, region: "europe", currency: "ISK" },
  GR: { rate: 0.05, region: "europe", currency: "EUR" },
  PL: { rate: 0.19, region: "europe", currency: "PLN" },
  CZ: { rate: 0.15, region: "europe", currency: "CZK" },
  HU: { rate: 0.15, region: "europe", currency: "HUF" },
  RO: { rate: 0.08, region: "europe", currency: "RON" },
  BG: { rate: 0.05, region: "europe", currency: "BGN" },
  HR: { rate: 0.12, region: "europe", currency: "EUR" },
  SI: { rate: 0.25, region: "europe", currency: "EUR" },
  SK: { rate: 0.07, region: "europe", currency: "EUR" },
  EE: { rate: 0, region: "europe", currency: "EUR" },
  LV: { rate: 0, region: "europe", currency: "EUR" },
  LT: { rate: 0.15, region: "europe", currency: "EUR" },
  CY: { rate: 0, region: "europe", currency: "EUR" },
  MT: { rate: 0, region: "europe", currency: "EUR" },
  TR: { rate: 0.1, region: "europe", currency: "TRY" },
  RU: { rate: 0.13, region: "europe", currency: "RUB" },
  UA: { rate: 0.05, region: "europe", currency: "UAH" },

  // Americas
  US: { rate: 0.15, region: "americas", currency: "USD" },
  CA: { rate: 0.25, region: "americas", currency: "CAD" },
  MX: { rate: 0.1, region: "americas", currency: "MXN" },
  BR: { rate: 0, region: "americas", currency: "BRL" },
  AR: { rate: 0.07, region: "americas", currency: "ARS" },
  CL: { rate: 0, region: "americas", currency: "CLP" },
  CO: { rate: 0.1, region: "americas", currency: "COP" },
  PE: { rate: 0.05, region: "americas", currency: "PEN" },
  UY: { rate: 0.07, region: "americas", currency: "UYU" },

  // Asia Pacific
  JP: { rate: 0.20315, region: "asiaPacific", currency: "JPY" },
  CN: { rate: 0.1, region: "asiaPacific", currency: "CNY" },
  HK: { rate: 0, region: "asiaPacific", currency: "HKD" },
  SG: { rate: 0, region: "asiaPacific", currency: "SGD" },
  KR: { rate: 0.154, region: "asiaPacific", currency: "KRW" },
  TW: { rate: 0.28, region: "asiaPacific", currency: "TWD" },
  AU: { rate: 0.3, region: "asiaPacific", currency: "AUD" },
  NZ: { rate: 0.33, region: "asiaPacific", currency: "NZD" },
  IN: { rate: 0.1, region: "asiaPacific", currency: "INR" },
  ID: { rate: 0.1, region: "asiaPacific", currency: "IDR" },
  MY: { rate: 0, region: "asiaPacific", currency: "MYR" },
  TH: { rate: 0.1, region: "asiaPacific", currency: "THB" },
  PH: { rate: 0.1, region: "asiaPacific", currency: "PHP" },
  VN: { rate: 0.05, region: "asiaPacific", currency: "VND" },
  PK: { rate: 0.15, region: "asiaPacific", currency: "PKR" },
  BD: { rate: 0.1, region: "asiaPacific", currency: "BDT" },

  // Middle East & Africa
  AE: { rate: 0, region: "mea", currency: "AED" },
  SA: { rate: 0, region: "mea", currency: "SAR" },
  QA: { rate: 0, region: "mea", currency: "QAR" },
  KW: { rate: 0, region: "mea", currency: "KWD" },
  BH: { rate: 0, region: "mea", currency: "BHD" },
  OM: { rate: 0, region: "mea", currency: "OMR" },
  // (IL intentionally omitted)
  EG: { rate: 0.1, region: "mea", currency: "EGP" },
  MA: { rate: 0.15, region: "mea", currency: "MAD" },
  TN: { rate: 0.1, region: "mea", currency: "TND" },
  DZ: { rate: 0.15, region: "mea", currency: "DZD" },
  JO: { rate: 0.05, region: "mea", currency: "JOD" },
  LB: { rate: 0.1, region: "mea", currency: "LBP" },
  ZA: { rate: 0.2, region: "mea", currency: "ZAR" },
  NG: { rate: 0.1, region: "mea", currency: "NGN" },
  KE: { rate: 0.15, region: "mea", currency: "KES" },
  GH: { rate: 0.08, region: "mea", currency: "GHS" },
  ET: { rate: 0.1, region: "mea", currency: "ETB" },
};

// Full ISO-3166 alpha-2 region buckets (countries without a documented rate
// inherit the regional average). Excludes obscure territories.
const EXTRA_BY_REGION: Record<Region, string[]> = {
  europe: [
    "AL", "AD", "BA", "BY", "GE", "GI", "LI", "MC", "MD", "ME", "MK", "RS",
    "SM", "VA", "XK", "AM", "AZ",
  ],
  americas: [
    "BO", "CR", "CU", "DO", "EC", "GT", "HN", "HT", "JM", "NI", "PA", "PY",
    "PR", "SV", "TT", "VE", "BS", "BB", "BZ", "GY", "SR",
  ],
  asiaPacific: [
    "AF", "BN", "BT", "FJ", "KH", "KZ", "KG", "LA", "LK", "MM", "MN", "MV",
    "NP", "PG", "TJ", "TM", "UZ", "WS", "TO", "VU", "SB", "MO", "TL",
  ],
  mea: [
    "AO", "BF", "BI", "BJ", "BW", "CD", "CF", "CG", "CI", "CM", "CV", "DJ",
    "ER", "GA", "GM", "GN", "GQ", "GW", "IQ", "IR", "LR", "LS", "LY", "MG",
    "ML", "MR", "MU", "MW", "MZ", "NA", "NE", "PS", "RW", "SC", "SD", "SL",
    "SN", "SO", "SS", "SZ", "TD", "TG", "TZ", "UG", "YE", "ZM", "ZW", "SY",
  ],
  other: [],
};

const buildList = (): DividendTaxRate[] => {
  const all = new Map<string, DividendTaxRate>();
  for (const [code, v] of Object.entries(KNOWN)) {
    all.set(code, { code, rate: v.rate, region: v.region, currency: v.currency });
  }
  for (const [region, codes] of Object.entries(EXTRA_BY_REGION) as [Region, string[]][]) {
    for (const code of codes) {
      if (!all.has(code)) {
        all.set(code, { code, rate: REGION_AVG[region], region, isEstimate: true });
      }
    }
  }
  return Array.from(all.values()).sort((a, b) => a.code.localeCompare(b.code));
};

export const DIVIDEND_TAX_RATES: DividendTaxRate[] = buildList();

// Synthetic entry for the "Custom rate" option in the picker.
export const CUSTOM_RATE_CODE = "CUSTOM";

export const getRate = (code: string): DividendTaxRate => {
  // Legacy alias: some links still use "UK" instead of ISO "GB".
  if (code === "UK") return getRate("GB");
  return (
    DIVIDEND_TAX_RATES.find((c) => c.code === code) ??
    DIVIDEND_TAX_RATES.find((c) => c.code === "US")!
  );
};

export const getCurrencyFor = (code: string): string => {
  const r = getRate(code);
  return r.currency ?? "EUR";
};
